import { useFormik } from "formik";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Loader from "../../Shared/Loader";
import { apiConnectorGet, apiConnectorPost } from "../../utils/APIConnector";
import { endpoint } from "../../utils/APIRoutes";
import { enCryptData } from "../../utils/Secret";
import { getFloatingValue } from "../../utils/utilityFun";
import logo1 from "../../assets/logo.png";

function Payout() {
  const [loding, setLoding] = useState(false);
  const location = useLocation();
  const navi = useNavigate();
  const client = useQueryClient();

  const params = new URLSearchParams(location?.search);
  const IdParam = params?.get("token");
  const base64String = atob(IdParam);

  const fk = useFormik({
    initialValues: { amount: "", walletAddress: "", wallet_type: "fund" },
    enableReinitialize: true,
  });

  async function handlePayout() {
    const reqbody = {
      wallet_address: String(fk.values.walletAddress)?.trim(),
      user_amount: Number(fk.values.amount)?.toFixed(3),
      wallet_type:
        fk.values.wallet_type === "fund"
          ? "fund_wallet"
          : fk.values.wallet_type === "profit"
          ? "income_wallet"
          : "topup_wallet",
    };

    if (!reqbody?.wallet_address || !reqbody?.user_amount) {
      Swal.fire({
        title: "Incomplete Information",
        html: `<p style="font-size:14px;">Please provide both wallet address and amount.</p>`,
        icon: "warning",
        confirmButtonColor: "#2563EB",
      });
      return;
    }

    setLoding(true);
    try {
      const res = await apiConnectorPost(
        endpoint?.member_payout,
        { payload: enCryptData(reqbody) },
        base64String
      );
      if (res.data?.success) fk.handleReset();
      Swal.fire({
        title: String(res?.data?.success) === "true" ? "Congratulations!" : "",
        html: `<p style="font-size:14px;">${res?.data?.message}</p>`,
        icon: String(res?.data?.success) === "true" ? "success" : "",
        confirmButtonColor: "#2563EB",
      });
      if (String(res?.data?.success) === "true") {
        client.refetchQueries("get_payout_report");
        client.refetchQueries("get_profile");
        navi("/payout-report");
      }
    } catch (e) {
      console.log(e);
    }
    setLoding(false);
  }

  const { data: profile } = useQuery(
    ["get_profile"],
    () => apiConnectorGet(endpoint?.profile_api),
    { refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }
  );
  const user_profile = profile?.data?.result?.[0] || [];

  const currentBalance = getFloatingValue(
    fk.values.wallet_type === "fund"
      ? user_profile?.tr03_fund_wallet
      : fk.values.wallet_type === "profit"
      ? user_profile?.tr03_inc_wallet
      : user_profile?.tr03_topup_wallet
  );

  const walletOptions = [
    { value: "fund", label: "Fund Wallet" },
    { value: "profit", label: "Profit Wallet" },
  ];

  return (
    <>
      <Loader isLoading={loding} />

      {/* ── Full-page background ── */}
      <div
        className="min-h-screen relative flex items-center justify-center py-10 px-4 overflow-x-hidden"
        style={{ background: "linear-gradient(160deg, #060E1C 0%, #080F1C 50%, #0B1E36 100%)" }}
      >
        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(37,99,235,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.03) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        {/* Ambient blobs */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-[120px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse, rgba(37,99,235,0.12) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(29,78,216,0.08) 0%, transparent 70%)" }}
        />

        {/* Ping particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#2563EB]/40 animate-ping pointer-events-none"
            style={{ top: `${15 + i * 13}%`, left: `${8 + i * 14}%`, animationDelay: `${i * 0.7}s`, animationDuration: "3s" }}
          />
        ))}

        {/* ── Card ── */}
        <div
          className="relative w-full max-w-md min-w-0 rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(145deg, rgba(7,21,38,0.95) 0%, rgba(8,15,28,0.95) 60%, rgba(11,30,54,0.95) 100%)",
            border: "1px solid rgba(37,99,235,0.25)",
            boxShadow: "0 0 0 1px rgba(37,99,235,0.08), 0 25px 60px rgba(0,0,0,0.6), 0 0 80px rgba(37,99,235,0.08)",
          }}
        >
          {/* Top accent */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2563EB]/70 to-transparent" />
          {/* Left accent bar */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#22D3EE] via-[#2563EB] to-[#1D4ED8]" />
          {/* Corner decorations */}
          <div className="absolute top-0 right-0 w-28 h-28 border-t border-r border-[#2563EB]/15 rounded-tr-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-20 h-20 border-b border-l border-[#1D4ED8]/10 rounded-bl-2xl pointer-events-none" />
          {/* Inner blobs */}
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(37,99,235,0.10) 0%, transparent 70%)" }} />
          <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full blur-2xl pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(29,78,216,0.07) 0%, transparent 70%)" }} />

          <div className="relative z-10 p-7">

            {/* ── Logo Header ── */}
            <div className="flex flex-col items-center mb-7">
              <div className="relative mb-3">
                <div className="absolute inset-0 bg-[#2563EB]/30 rounded-2xl blur-lg" />
                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2563EB]/20 to-[#1D4ED8]/20 border border-[#2563EB]/40 flex items-center justify-center">
                  <img src={logo1} alt="logo" className="w-9 h-9 object-contain drop-shadow" />
                </div>
              </div>
              <h2 className="text-white font-extrabold text-xl tracking-wide">Fund Withdrawal</h2>
              <p className="text-[#475569] text-xs mt-1 tracking-wide">Request payout · USDT BEP-20</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#22D3EE] animate-pulse" />
                <span className="text-[#7DD3FC] text-[10px] font-semibold tracking-widest uppercase">Secure Transaction</span>
              </div>
            </div>

            {/* ── Balance Card ── */}
            <div
              className="relative rounded-xl p-4 mb-5 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(29,78,216,0.08) 100%)",
                border: "1px solid rgba(37,99,235,0.25)",
              }}
            >
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 70%)" }} />
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-[#475569] text-[10px] uppercase tracking-widest font-semibold mb-1">Available Balance</p>
                  <p className="text-white font-extrabold text-2xl">
                    <span className="text-[#22D3EE] text-lg mr-1">$</span>
                    {currentBalance}
                  </p>
                  <p className="text-[#334155] text-[10px] mt-1">USDT · BEP-20</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] flex items-center justify-center shadow-[0_0_16px_rgba(37,99,235,0.4)]">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* ── Wallet Type Selector ── */}
            <div className="mb-5">
              <p className="text-[#475569] text-[10px] uppercase tracking-widest font-semibold mb-2.5">Withdraw From</p>
              <div className="grid grid-cols-2 gap-3">
                {walletOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                      fk.values.wallet_type === opt.value
                        ? "border-[#2563EB]/60 bg-[#2563EB]/10 text-white"
                        : "border-[#1e3a5f]/50 bg-[#060E1C]/40 text-[#64748B] hover:border-[#1e3a5f] hover:text-[#94A3B8]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="wallet_type"
                      value={opt.value}
                      onChange={fk.handleChange}
                      checked={fk.values.wallet_type === opt.value}
                      className="hidden"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                      fk.values.wallet_type === opt.value ? "border-[#2563EB]" : "border-[#334155]"
                    }`}>
                      {fk.values.wallet_type === opt.value && (
                        <div className="w-2 h-2 rounded-full bg-[#2563EB]" />
                      )}
                    </div>
                    <span className="text-xs font-semibold">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ── Wallet Address ── */}
            <div className="mb-4">
              <label className="text-[#475569] text-[10px] uppercase tracking-widest font-semibold block mb-2">
                Wallet Address (USDT · BEP-20)
              </label>
              <input
                placeholder="0x... BEP20 wallet address"
                id="walletAddress"
                name="walletAddress"
                value={fk.values.walletAddress}
                onChange={fk.handleChange}
                className="w-full px-4 py-3 rounded-xl text-white text-sm font-mono placeholder-[#334155] focus:outline-none transition-all duration-200"
                style={{ background: "rgba(6,14,28,0.8)", border: "1px solid rgba(30,58,95,0.6)" }}
                onFocus={(e) => {
                  e.target.style.border = "1px solid rgba(37,99,235,0.6)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(37,99,235,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.border = "1px solid rgba(30,58,95,0.6)";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* ── Amount Input ── */}
            <div className="mb-4">
              <label className="text-[#475569] text-[10px] uppercase tracking-widest font-semibold block mb-2">
                Withdrawal Amount
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#2563EB] font-bold text-sm pointer-events-none">$</div>
                <input
                  placeholder="0.00"
                  id="amount"
                  name="amount"
                  value={fk.values.amount}
                  onChange={fk.handleChange}
                  type="number"
                  min="0"
                  className="w-full pl-8 pr-16 py-3 rounded-xl text-white text-base font-semibold placeholder-[#334155] focus:outline-none transition-all duration-200"
                  style={{ background: "rgba(6,14,28,0.8)", border: "1px solid rgba(30,58,95,0.6)" }}
                  onFocus={(e) => {
                    e.target.style.border = "1px solid rgba(37,99,235,0.6)";
                    e.target.style.boxShadow = "0 0 0 3px rgba(37,99,235,0.12)";
                  }}
                  onBlur={(e) => {
                    e.target.style.border = "1px solid rgba(30,58,95,0.6)";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#334155] text-xs font-semibold pointer-events-none">USDT</div>
              </div>

              {/* Quick amount buttons */}
              <div className="mt-2.5 grid grid-cols-4 gap-2">
                {[25, 50, 100, "Max"].map((amt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() =>
                      fk.setFieldValue("amount", amt === "Max" ? currentBalance : amt)
                    }
                    className="py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 border border-[#1e3a5f]/50 text-[#475569] hover:border-[#2563EB]/40 hover:text-[#7DD3FC] hover:bg-[#2563EB]/8"
                    style={{ background: "rgba(6,14,28,0.6)" }}
                  >
                    {amt === "Max" ? "Max" : `$${amt}`}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Warning Notice ── */}
            <div className="mb-6 flex items-start gap-3 rounded-xl p-3.5"
              style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)" }}>
              <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-amber-400 text-[10px] font-bold uppercase tracking-widest mb-1">Important Notice</p>
                <p className="text-amber-400/70 text-xs leading-relaxed">
                  Withdrawal requests are processed within 24–48 hours. Minimum withdrawal amount is $10.
                </p>
              </div>
            </div>

            {/* ── Submit Button ── */}
            <button
              onClick={handlePayout}
              type="button"
              className="relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                boxShadow: "0 0 20px rgba(37,99,235,0.35)",
              }}
            >
              {/* Shimmer */}
              <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <span className="relative z-10 flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Request Withdrawal
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>

            {/* ── SSL note ── */}
            <div className="mt-4 flex items-center justify-center gap-1.5 text-[#334155] text-[10px]">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Secured with SSL encryption</span>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default Payout;
